import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { lockProgramData } from '../../services/rpc';
import { ContentEntryForm } from './ContentEntryForm';
import { StructuredDataForm } from './StructuredDataForm';
import { ValidationPanel } from './ValidationPanel';
import { PreviewPanel } from './PreviewPanel';
import { ManualEntryHistory } from './ManualEntryHistory';
import { BatchImportModal } from './BatchImportModal';
import type {
  ManualEntryStructuredData,
  ManualEntryCategory,
  ManualEntryValidationResult,
} from './manualEntryTypes';
import {
  buildStructuredContent,
  createEmptyStructuredData,
  hasStructuredData,
  mergeCategories,
  normaliseStructuredData,
} from './manualEntryUtils';

interface SupabaseRpcLike {
  rpc<T = unknown>(fn: string, args?: Record<string, unknown>): Promise<{ data: T | null; error: unknown }>;
}

const supabaseRpc = supabase as unknown as SupabaseRpcLike;

const describeError = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in (error as Record<string, unknown>)) {
    const message = (error as Record<string, unknown>).message;
    if (typeof message === 'string') return message;
  }
  return 'Unknown error';
};

interface RawProgramMetric {
  program_id?: string;
  program_name?: string | null;
  categories_missing_display?: number | null;
  website_url?: string | null;
  seed_source_url?: string | null;
  baci_program_code?: string | null;
}

type SaveManualEntryResponse =
  | string
  | {
      snapshot_id?: string;
      program_id?: string;
      created_program?: boolean;
      snapshotId?: string;
      programId?: string;
      createdProgram?: boolean;
    };

interface ProgramOption {
  id: string;
  name: string;
  missingDisplay: number;
  websiteUrl?: string | null;
  seedUrl?: string | null;
  programCode?: string | null;
}

type ProgramMode = 'existing' | 'new';

interface ProgramCategoryOption {
  id: string;
  label: string;
}

interface RawCategoryRow {
  id?: string;
  name?: string | null;
  category_display_name?: string | null;
}

interface RawProgramCheckRow {
  id?: string;
  name?: string | null;
  baci_program_code?: string | null;
}

interface ManualFlaggedProgram {
  program_id?: string;
  program_name?: string | null;
  categories_missing_display?: number | null;
}

const formatProgramLabel = (program: ProgramOption): string => {
  if (program.missingDisplay > 0) {
    return `${program.name} — ${program.missingDisplay} categories missing display`;
  }
  return program.name;
};

export function ManualEntryDashboard() {
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [programMode, setProgramMode] = useState<ProgramMode>('existing');
  const [programSearch, setProgramSearch] = useState('');
  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [validationResult, setValidationResult] = useState<ManualEntryValidationResult | null>(null);
  const [content, setContent] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [structuredData, setStructuredData] = useState<ManualEntryStructuredData>(createEmptyStructuredData());
  const [operatorNotes, setOperatorNotes] = useState('');
  const [confidenceLevel, setConfidenceLevel] = useState<'high' | 'medium' | 'low'>('medium');
  const [activeTab, setActiveTab] = useState<'raw' | 'structured' | 'preview' | 'history'>('raw');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [autoLockAfterSave, setAutoLockAfterSave] = useState(true);
  const [isBatchModalOpen, setBatchModalOpen] = useState(false);
  const [batchPreviewCount, setBatchPreviewCount] = useState<number | null>(null);
  const [newProgramName, setNewProgramName] = useState('');
  const [newProgramWebsite, setNewProgramWebsite] = useState('');
  const [programCategories, setProgramCategories] = useState<ProgramCategoryOption[]>([]);
  const [categorySelectId, setCategorySelectId] = useState('');
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [existingMatches, setExistingMatches] = useState<ProgramOption[]>([]);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [hasCheckedExisting, setHasCheckedExisting] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [manualReportLoading, setManualReportLoading] = useState(false);
  const [manualReportError, setManualReportError] = useState<string | null>(null);

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      const { data, error } = await supabaseRpc.rpc<RawProgramMetric[]>('get_program_report_metrics');

      if (error) throw error;

      const mapped = (data ?? [])
        .map((item) => ({
          id: item.program_id ?? '',
          name: item.program_name ?? 'Unnamed program',
          missingDisplay: item.categories_missing_display ?? 0,
          websiteUrl: item.website_url ?? null,
          seedUrl: item.seed_source_url ?? null,
          programCode: item.baci_program_code ?? null,
        }))
        .filter((program) => program.id && program.name);

      setPrograms(mapped);
    } catch (error) {
      console.error('Error loading programs:', error);
      setMessage({ type: 'error', text: 'Failed to load programs' });
    }
  };

  const sortedPrograms = useMemo(() => {
    return [...programs].sort((a, b) => {
      if (b.missingDisplay !== a.missingDisplay) {
        return b.missingDisplay - a.missingDisplay;
      }
      return a.name.localeCompare(b.name);
    });
  }, [programs]);

  const filteredPrograms = useMemo(() => {
    const term = programSearch.trim().toLowerCase();
    if (!term) return sortedPrograms;
    return sortedPrograms.filter((program) =>
      program.name.toLowerCase().includes(term) || program.id.toLowerCase().includes(term)
    );
  }, [sortedPrograms, programSearch]);

  const selectedProgramData = useMemo(
    () => sortedPrograms.find((program) => program.id === selectedProgram),
    [sortedPrograms, selectedProgram]
  );

  const programNameForPreview =
    programMode === 'existing' ? selectedProgramData?.name : newProgramName || undefined;

  const trimmedContent = content.trim();
  const hasStructured = hasStructuredData(structuredData);
  const previewContent = useMemo(() => {
    if (trimmedContent) return trimmedContent;
    if (hasStructured) return buildStructuredContent(programNameForPreview, structuredData);
    return '';
  }, [trimmedContent, hasStructured, programNameForPreview, structuredData]);

  const canSubmit = Boolean(
    (programMode === 'existing'
      ? selectedProgram
      : newProgramName.trim().length > 0 && hasCheckedExisting) &&
      (trimmedContent.length > 0 || hasStructured)
  );

  const compileSubmissionContent = () => {
    if (trimmedContent) {
      return { submissionContent: trimmedContent, usedStructuredSummary: false };
    }

    if (hasStructured) {
      return {
        submissionContent: buildStructuredContent(programNameForPreview, structuredData),
        usedStructuredSummary: true,
      };
    }

    return { submissionContent: '', usedStructuredSummary: false };
  };

  const handleStructuredChange = (data: ManualEntryStructuredData) => {
    setStructuredData(normaliseStructuredData(data));
  };

  const handleBatchImportSuccess = (categories: ManualEntryCategory[]) => {
    setStructuredData((prev) => {
      const normalised = normaliseStructuredData(prev);
      const mergedCategories = mergeCategories(normalised.categories, categories);
      return {
        ...normalised,
        categories: mergedCategories,
      };
    });
    setBatchPreviewCount(categories.length);
    setBatchModalOpen(false);
    setActiveTab('structured');
    setMessage({ type: 'success', text: `Imported ${categories.length} categories from batch upload.` });
  };

  const resetForm = () => {
    setContent('');
    setSourceUrl('');
    setStructuredData(createEmptyStructuredData());
    setOperatorNotes('');
    setValidationResult(null);
    setBatchPreviewCount(null);
    if (programMode === 'new') {
      setNewProgramName('');
      setNewProgramWebsite('');
    }
    setProgramCategories([]);
    setCategorySelectId('');
    setExistingMatches([]);
    setCheckingExisting(false);
    setHasCheckedExisting(false);
    setCheckError(null);
  };

  const validateEntry = async () => {
    if (programMode === 'existing' && !selectedProgram) {
      setMessage({ type: 'error', text: 'Please select a program to validate.' });
      return;
    }

    if (programMode === 'new' && newProgramName.trim().length === 0) {
      setMessage({ type: 'error', text: 'Enter a program name before validating.' });
      return;
    }

    if (programMode === 'new' && !hasCheckedExisting) {
      setMessage({ type: 'error', text: 'Run the existing program check before validating.' });
      return;
    }

    const { submissionContent, usedStructuredSummary } = compileSubmissionContent();

    if (!submissionContent) {
      setMessage({ type: 'error', text: 'Add raw content or structured data before validating.' });
      return;
    }

    try {
      const { data, error } = await supabaseRpc.rpc<ManualEntryValidationResult>('validate_manual_entry', {
        p_program_id: programMode === 'existing' ? selectedProgram : null,
        p_content: submissionContent,
        p_structured_data: hasStructured ? structuredData : null,
      });

      if (error) throw error;

      if (data) {
        setValidationResult({ ...data, usedStructuredSummary });
      } else {
        setValidationResult(null);
      }

      if (usedStructuredSummary) {
        setMessage({
          type: 'success',
          text: 'Validation succeeded using structured data summary as the snapshot content.',
        });
      } else {
        setMessage({ type: 'success', text: 'Validation succeeded.' });
      }
    } catch (error) {
      console.error('Validation error:', error);
      setMessage({ type: 'error', text: 'Failed to validate entry' });
    }
  };

  const saveManualEntry = async () => {
    if (programMode === 'existing' && !selectedProgram) {
      setMessage({ type: 'error', text: 'Please select a program before saving.' });
      return;
    }

    if (programMode === 'new' && newProgramName.trim().length === 0) {
      setMessage({ type: 'error', text: 'Enter a program name before saving.' });
      return;
    }

    if (programMode === 'new' && !hasCheckedExisting) {
      setMessage({ type: 'error', text: 'Run the existing program check before saving.' });
      return;
    }

    const { submissionContent, usedStructuredSummary } = compileSubmissionContent();

    if (!submissionContent) {
      setMessage({ type: 'error', text: 'Add raw content or structured data before saving.' });
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabaseRpc.rpc<SaveManualEntryResponse>('save_manual_entry', {
        p_program_id: programMode === 'existing' ? selectedProgram : null,
        p_content: submissionContent,
        p_structured_data: hasStructured ? structuredData : null,
        p_source_url: sourceUrl || null,
        p_operator_notes: operatorNotes || null,
        p_confidence_level: confidenceLevel,
        p_program_name: programMode === 'new' ? newProgramName.trim() : null,
        p_program_website: programMode === 'new' ? (newProgramWebsite.trim() || null) : null,
      });

      if (error) throw error;

      const payload = data;
      let snapshotId: string | undefined;
      let savedProgramId: string | undefined = programMode === 'existing' ? selectedProgram : undefined;
      let createdProgram = false;

      const payloadObject =
        payload && typeof payload === 'object' && !Array.isArray(payload)
          ? (payload as Record<string, unknown>)
          : null;

      if (payloadObject) {
        snapshotId = (payloadObject.snapshot_id ?? payloadObject.snapshotId) as string | undefined;
        savedProgramId = (payloadObject.program_id ?? payloadObject.programId ?? savedProgramId) as
          | string
          | undefined;
        const createdFlag = payloadObject.created_program ?? payloadObject.createdProgram;
        createdProgram = Boolean(
          typeof createdFlag === 'boolean' ? createdFlag : programMode === 'new'
        );
      } else if (typeof payload === 'string') {
        snapshotId = payload;
      }

      const fallbackNotice = usedStructuredSummary
        ? ' Saved using structured data summary (no raw content provided).'
        : '';

      let successText = `Manual entry saved. Snapshot ID: ${snapshotId ?? 'unknown'}.${fallbackNotice}`;
      if (createdProgram && savedProgramId) {
        successText += ` New program created with ID: ${savedProgramId}.`;
      }

      let messageText = successText;
      let messageType: 'success' | 'error' = 'success';

      if (autoLockAfterSave && savedProgramId) {
        try {
          const lockResponse = await lockProgramData(savedProgramId, 'manual_review_complete', {
            source: 'manual_entry',
            snapshot_id: snapshotId ?? null,
            structured_summary_used: usedStructuredSummary,
            operator_notes: operatorNotes || null,
          });
          if (lockResponse.success) {
            messageText += ' Program locked for manual review.';
          } else {
            messageType = 'error';
            messageText += ` Program lock failed: ${lockResponse.error ?? 'Unknown error.'}`;
          }
        } catch (lockError) {
          messageType = 'error';
          messageText += ` Program lock failed: ${describeError(lockError)}.`;
        }
      }

      if (createdProgram && savedProgramId && programMode === 'new') {
        setPrograms((prev) => [
          ...prev,
          {
            id: savedProgramId,
            name: newProgramName.trim(),
            missingDisplay: structuredData.categories.length,
            websiteUrl: newProgramWebsite.trim() || null,
            seedUrl: null,
            programCode: null,
          },
        ]);
        setSelectedProgram(savedProgramId);
        setProgramMode('existing');
        setProgramSearch('');
      }

      setMessage({ type: messageType, text: messageText });
      resetForm();
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Failed to save manual entry' });
    } finally {
      setSaving(false);
    }
  };

  const handleProgramModeChange = (mode: ProgramMode) => {
    setProgramMode(mode);
    setMessage(null);
    setValidationResult(null);
    if (mode === 'existing') {
      setSelectedProgram('');
    } else {
      setProgramSearch('');
      setSelectedProgram('');
    }
    setProgramCategories([]);
    setCategorySelectId('');
  };

  useEffect(() => {
    if (programMode !== 'existing' || !selectedProgram) {
      setProgramCategories([]);
      setCategorySelectId('');
      return;
    }

    let isActive = true;

    const fetchCategories = async () => {
      setCategoryLoading(true);
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('id, name, category_display_name')
          .eq('program_id', selectedProgram)
          .order('category_display_name', { ascending: true })
          .order('name', { ascending: true });

        if (!isActive) return;

        if (error) {
          console.error('Failed to load categories', error);
          setProgramCategories([]);
          setCategorySelectId('');
          return;
        }

        const rawCategories = (data ?? []) as RawCategoryRow[];
        const mapped = rawCategories
          .map((item) => ({
            id: String(item.id ?? ''),
            label: item.category_display_name || item.name || 'Untitled category',
          }))
          .filter((category) => category.id);

        setProgramCategories(mapped);
        setCategorySelectId((current) =>
          current && mapped.some((cat) => cat.id === current) ? current : mapped[0]?.id ?? ''
        );
      } catch (error) {
        if (!isActive) return;
        console.error('Failed to load categories', error);
        setProgramCategories([]);
        setCategorySelectId('');
      } finally {
        if (isActive) setCategoryLoading(false);
      }
    };

    fetchCategories();

    return () => {
      isActive = false;
    };
  }, [programMode, selectedProgram]);

  const handleOpenProgramCard = () => {
    const code = selectedProgramData?.programCode;
    if (!code) return;
    const url = `${window.location.origin}/#program-card?program_code=${encodeURIComponent(code)}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleOpenCategoryCard = () => {
    if (!categorySelectId) return;
    const url = `${window.location.origin}/#category-card?category_id=${encodeURIComponent(categorySelectId)}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleCheckExistingPrograms = async () => {
    const trimmed = newProgramName.trim();
    if (trimmed.length < 3) {
      setCheckError('Enter at least 3 characters before checking.');
      setExistingMatches([]);
      setHasCheckedExisting(false);
      return;
    }

    setCheckingExisting(true);
    setCheckError(null);
    try {
      const { data, error } = await supabase
        .from('programs')
        .select('id, name, baci_program_code')
        .ilike('name', `%${trimmed}%`)
        .limit(10);

      setHasCheckedExisting(true);

      if (error) {
        console.error('Failed to check programs', error);
        setCheckError('Unable to search existing programs. Try again.');
        setExistingMatches([]);
        return;
      }

      const raw = (data ?? []) as RawProgramCheckRow[];
      const mapped = raw
        .map((item) => ({
          id: String(item.id ?? ''),
          name: item.name ?? 'Unnamed program',
          missingDisplay: 0,
          websiteUrl: null,
          seedUrl: null,
          programCode: item.baci_program_code ?? null,
        }))
        .filter((program) => program.id && program.name);

      setExistingMatches(mapped);

      if (mapped.length > 0) {
        setMessage({
          type: 'error',
          text: 'Possible duplicates found. Review the matches below before creating a new program.',
        });
      } else {
        setMessage({
          type: 'success',
          text: 'No existing program found. You can proceed to create a new one.',
        });
      }
    } catch (error) {
      console.error('Failed to check programs', error);
      setCheckError('Unable to search existing programs. Try again.');
      setExistingMatches([]);
      setHasCheckedExisting(false);
    } finally {
      setCheckingExisting(false);
    }
  };

  const handleOpenManualReport = async () => {
    const reportWindow = window.open('about:blank', '_blank');
    if (!reportWindow) {
      setManualReportError('Unable to open report window. Please allow pop-ups for this site.');
      return;
    }

    reportWindow.document.write('<html><head><title>Manual Entry Report</title></head><body>');
    reportWindow.document.write('<h1>Programs Flagged for Manual Entry</h1>');
    reportWindow.document.write('<p>Generating report…</p>');
    reportWindow.document.write('</body></html>');
    reportWindow.document.close();

    setManualReportLoading(true);
    setManualReportError(null);
    try {
      const { data, error } = await supabaseRpc.rpc<ManualFlaggedProgram[]>(
        'get_program_report_metrics',
        { p_seed_only: false, p_limit: 500 }
      );

      if (error) throw error;

      const flagged = (data ?? [])
        .filter((row) => (row.categories_missing_display ?? 0) > 0)
        .map((row) => ({
          program_id: row.program_id,
          program_name: row.program_name,
          categories_missing_display: row.categories_missing_display ?? 0,
        }));

      reportWindow.document.open();
      reportWindow.document.write('<html><head><title>Manual Entry Report</title></head><body>');
      reportWindow.document.write('<h1>Programs Flagged for Manual Entry</h1>');
      reportWindow.document.write('<p>Programs with missing category display names.</p>');
      if (flagged.length === 0) {
        reportWindow.document.write('<p>No programs currently flagged.</p>');
      } else {
        reportWindow.document.write('<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;">');
        reportWindow.document.write('<thead><tr><th>Program</th><th>Missing Category Displays</th></tr></thead>');
        reportWindow.document.write('<tbody>');
        flagged.forEach((item) => {
          reportWindow.document.write(
            `<tr><td>${item.program_name ?? item.program_id}</td><td>${item.categories_missing_display}</td></tr>`
          );
        });
        reportWindow.document.write('</tbody></table>');
      }
      reportWindow.document.write('</body></html>');
      reportWindow.document.close();
    } catch (error) {
      console.error('Failed to load manual report', error);
      setManualReportError('Could not load manual report. Try again later.');
      reportWindow.document.open();
      reportWindow.document.write('<html><head><title>Manual Entry Report</title></head><body>');
      reportWindow.document.write('<h1>Programs Flagged for Manual Entry</h1>');
      reportWindow.document.write('<p style="color:#b91c1c;">Could not load manual report. Try again later.</p>');
      reportWindow.document.write('</body></html>');
      reportWindow.document.close();
    } finally {
      setManualReportLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>Manual</h1>
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>
          Enter award program data manually when automated crawling fails or for programs with complex structures.
        </p>

        {message && (
          <div style={{
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '16px',
            backgroundColor: message.type === 'error' ? '#fee' : '#efe',
            color: message.type === 'error' ? '#c00' : '#080',
            border: `1px solid ${message.type === 'error' ? '#fcc' : '#cfc'}`
          }}>
            {message.text}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '16px'
        }}>
          <button
            type="button"
            onClick={() => handleProgramModeChange('existing')}
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: '6px',
              border: programMode === 'existing' ? '1px solid #059669' : '1px solid #d1d5db',
              backgroundColor: programMode === 'existing' ? '#ecfdf5' : 'white',
              color: programMode === 'existing' ? '#065f46' : '#374151',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Select Existing Program
          </button>
          <button
            type="button"
            onClick={() => handleProgramModeChange('new')}
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: '6px',
              border: programMode === 'new' ? '1px solid #2563eb' : '1px solid #d1d5db',
              backgroundColor: programMode === 'new' ? '#eff6ff' : 'white',
              color: programMode === 'new' ? '#1d4ed8' : '#374151',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            Add New Program
          </button>
        </div>

        {programMode === 'existing' ? (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Select Program
            </label>
            <input
              type="search"
              placeholder="Search by program name or ID"
              value={programSearch}
              onChange={(e) => setProgramSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '8px'
              }}
            />
            <select
              value={selectedProgram}
              onChange={(e) => setSelectedProgram(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">Choose a program...</option>
              {filteredPrograms.map((program) => (
                <option key={program.id} value={program.id}>
                  {formatProgramLabel(program)}
                </option>
              ))}
            </select>
            {filteredPrograms.length === 0 && (
              <div style={{ marginTop: '8px', color: '#b45309', fontSize: '13px' }}>
                No programs match your search. Switch to "Add New Program" to create a new record.
              </div>
            )}
          </div>
        ) : (
          <div style={{
            marginBottom: '16px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            padding: '16px',
            backgroundColor: '#f9fafb'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
              New Program Details
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
                  Program Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  value={newProgramName}
                  onChange={(e) => {
                    setNewProgramName(e.target.value);
                    setHasCheckedExisting(false);
                    setExistingMatches([]);
                    setCheckError(null);
                  }}
                  placeholder="Pulitzer Prize"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  Step 1: Search existing programs before creating a new record.
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                  <button
                    type="button"
                    onClick={handleCheckExistingPrograms}
                    disabled={checkingExisting}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #1f2937',
                      backgroundColor: '#1f2937',
                      color: 'white',
                      cursor: checkingExisting ? 'wait' : 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {checkingExisting ? 'Checking…' : 'Check existing programs'}
                  </button>
                  {hasCheckedExisting && existingMatches.length === 0 && !checkError && (
                    <span style={{ fontSize: '12px', color: '#047857' }}>No matches found.</span>
                  )}
                  {checkError && (
                    <span style={{ fontSize: '12px', color: '#b91c1c' }}>{checkError}</span>
                  )}
                </div>
                {existingMatches.length > 0 && (
                  <div style={{
                    marginTop: '8px',
                    border: '1px solid #f97316',
                    backgroundColor: '#fff7ed',
                    borderRadius: '6px',
                    padding: '10px'
                  }}>
                    <div style={{ fontSize: '12px', color: '#9a3412', marginBottom: '4px', fontWeight: 600 }}>
                      Possible matches found:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
                      {existingMatches.map((match) => (
                        <li key={match.id} style={{ fontSize: '12px', color: '#7c2d12' }}>
                          {match.name}
                          <button
                            type="button"
                            onClick={() => {
                              setProgramMode('existing');
                              setProgramSearch(match.name);
                              setSelectedProgram(match.id);
                              setExistingMatches([]);
                              setHasCheckedExisting(false);
                              setMessage({ type: 'success', text: `Switched to existing program ${match.name}.` });
                            }}
                            style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              border: '1px solid #1d4ed8',
                              backgroundColor: '#1d4ed8',
                              color: '#ffffff',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Use this program
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
                  Program Website (optional)
                </label>
                <input
                  value={newProgramWebsite}
                  onChange={(e) => setNewProgramWebsite(e.target.value)}
                  placeholder="https://www.example.org/awards"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  The manual entry flow will create the program record automatically using these details.
                </p>
              </div>
            </div>
          </div>
        )}

        {programMode === 'existing' && selectedProgramData && (
          <div style={{
            display: 'grid',
            gap: '8px',
            marginBottom: '16px'
          }}>
            {selectedProgramData.missingDisplay > 0 && (
              <div style={{
                padding: '12px',
                borderRadius: '6px',
                backgroundColor: '#fff7ed',
                color: '#c2410c',
                border: '1px solid #fed7aa'
              }}>
                {selectedProgramData.name} has {selectedProgramData.missingDisplay} categories without display names.
              </div>
            )}

            {(selectedProgramData.seedUrl || selectedProgramData.websiteUrl) && (
              <div style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '12px',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
                  Quick links for copying source content:
                </div>
                {selectedProgramData.seedUrl && (
                  <div style={{ fontSize: '13px', display: 'flex', gap: '6px' }}>
                    <strong style={{ color: '#0f766e' }}>Seed URL:</strong>
                    <a
                      href={selectedProgramData.seedUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#2563eb', textDecoration: 'underline' }}
                    >
                      {selectedProgramData.seedUrl}
                    </a>
                  </div>
                )}
                {selectedProgramData.websiteUrl && (
                  <div style={{ fontSize: '13px', display: 'flex', gap: '6px', marginTop: '4px' }}>
                    <strong style={{ color: '#0f172a' }}>Program Site:</strong>
                    <a
                      href={selectedProgramData.websiteUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: '#2563eb', textDecoration: 'underline' }}
                    >
                      {selectedProgramData.websiteUrl}
                    </a>
                  </div>
                )}
              </div>
            )}

            <div style={{
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#f3f4f6'
            }}>
              <div style={{ fontSize: '13px', color: '#111827', marginBottom: '8px', fontWeight: 500 }}>
                Data gap helpers:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={handleOpenProgramCard}
                    disabled={!selectedProgramData.programCode}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #0f766e',
                      backgroundColor: selectedProgramData.programCode ? '#0f766e' : '#d1d5db',
                      color: selectedProgramData.programCode ? '#ffffff' : '#6b7280',
                      cursor: selectedProgramData.programCode ? 'pointer' : 'not-allowed',
                      fontSize: '13px'
                    }}
                  >
                    Program Card
                  </button>

                  <button
                    type="button"
                    onClick={handleOpenManualReport}
                    disabled={manualReportLoading}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #7c2d12',
                      backgroundColor: '#7c2d12',
                      color: '#ffffff',
                      cursor: manualReportLoading ? 'wait' : 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {manualReportLoading ? 'Generating…' : 'Manual Report'}
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <select
                    value={categorySelectId}
                    onChange={(e) => setCategorySelectId(e.target.value)}
                    disabled={categoryLoading || programCategories.length === 0}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #d1d5db',
                      fontSize: '13px',
                      minWidth: '220px',
                      backgroundColor: '#ffffff'
                    }}
                  >
                    {programCategories.length === 0 ? (
                      <option value="">
                        {categoryLoading ? 'Loading categories…' : 'No categories found'}
                      </option>
                    ) : (
                      programCategories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.label}
                        </option>
                      ))
                    )}
                  </select>
                  <button
                    type="button"
                    onClick={handleOpenCategoryCard}
                    disabled={!categorySelectId}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: '1px solid #1d4ed8',
                      backgroundColor: categorySelectId ? '#1d4ed8' : '#d1d5db',
                      color: categorySelectId ? '#ffffff' : '#6b7280',
                      cursor: categorySelectId ? 'pointer' : 'not-allowed',
                      fontSize: '13px'
                    }}
                  >
                    Category Card
                  </button>
                </div>
              </div>
              {manualReportError && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#b91c1c' }}>
                  {manualReportError}
                </div>
              )}
            </div>
          </div>
        )}

        {(selectedProgram || programMode === 'new') && (
          <>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                display: 'flex',
                gap: '1px',
                backgroundColor: '#e5e7eb',
                borderRadius: '6px',
                padding: '1px',
                marginBottom: '24px'
              }}>
                {(['raw', 'structured', 'preview', 'history'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      border: 'none',
                      backgroundColor: activeTab === tab ? 'white' : 'transparent',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: activeTab === tab ? '500' : '400',
                      color: activeTab === tab ? '#111827' : '#6b7280',
                      transition: 'all 0.2s'
                    }}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    {tab === 'raw' && ' Content'}
                    {tab === 'structured' && ' Data'}
                  </button>
                ))}
              </div>

              <div style={{ minHeight: '400px' }}>
                {activeTab === 'raw' && (
                  <ContentEntryForm
                    content={content}
                    sourceUrl={sourceUrl}
                    operatorNotes={operatorNotes}
                    confidenceLevel={confidenceLevel}
                    onContentChange={setContent}
                    onSourceUrlChange={setSourceUrl}
                    onOperatorNotesChange={setOperatorNotes}
                    onConfidenceLevelChange={setConfidenceLevel}
                  />
                )}
                {activeTab === 'structured' && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                      <button
                        onClick={() => setBatchModalOpen(true)}
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Batch Import (CSV / JSON)
                      </button>
                    </div>
                    <StructuredDataForm data={structuredData} onChange={handleStructuredChange} />
                  </>
                )}
                {activeTab === 'preview' && (
                  <PreviewPanel
                    programId={programMode === 'existing' ? selectedProgram : 'pending'}
                    programName={programNameForPreview}
                    content={previewContent}
                    structuredData={structuredData}
                    sourceUrl={sourceUrl || (programMode === 'new' ? newProgramWebsite : '')}
                  />
                )}
                {activeTab === 'history' && programMode === 'existing' && (
                  <ManualEntryHistory programId={selectedProgram} />
                )}
                {activeTab === 'history' && programMode === 'new' && (
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '32px',
                    border: '1px solid #e5e7eb',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    History will be available after the program is created and entries are saved.
                  </div>
                )}
              </div>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <ValidationPanel validation={validationResult} />
                <label style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={autoLockAfterSave}
                    onChange={(event) => setAutoLockAfterSave(event.target.checked)}
                  />
                  <span>Lock program after save</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={validateEntry}
                  disabled={!canSubmit || saving}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: (!canSubmit || saving) ? 'not-allowed' : 'pointer',
                    opacity: (!canSubmit || saving) ? 0.5 : 1
                  }}
                >
                  Validate
                </button>
                <button
                  onClick={saveManualEntry}
                  disabled={!canSubmit || saving}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    cursor: (!canSubmit || saving) ? 'not-allowed' : 'pointer',
                    opacity: (!canSubmit || saving) ? 0.5 : 1
                  }}
                >
                  {saving ? 'Saving...' : 'Save Entry'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <BatchImportModal
        open={isBatchModalOpen}
        onClose={() => setBatchModalOpen(false)}
        onImport={handleBatchImportSuccess}
        existingCount={structuredData.categories.length}
      />

      {batchPreviewCount !== null && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          borderRadius: '6px',
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          color: '#1d4ed8'
        }}>
          Last batch import added {batchPreviewCount} categories.
        </div>
      )}
    </div>
  );
}
